import React, { useEffect, useState } from 'react';
import { History, Moon, ChevronDown, ChevronUp } from 'lucide-react';
import { useContact } from '../../context/ContactContext';
import { displaySummary } from '../../lib/summary';
import { apiHeaders } from '../../lib/consoleApi';
import { loadCallerHistory, rowDuration, NO_TENANT, ERROR, EMPTY, OK } from '../../lib/callerHistory';

const DATA_URL = import.meta.env.VITE_DATA_URL || '';

// Display cap (2026-09-14): the centre panel is a fixed-height column with
// overflow hidden, so an uncapped history list on a frequent caller pushed the
// live transcript clean out of view during a call. Show the newest rows only;
// the rest sit behind a toggle, and even expanded the list scrolls inside a
// capped box (callbacks-history.css) rather than growing the card. Data and
// fetch logic are unchanged — this is presentation only.
const VISIBLE_ROWS = 3;

function fmt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-AU', {
    day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
    hour12: true, timeZone: 'Australia/Sydney',
  });
}

// Per-caller history for the connected contact: prior after-hours attempts
// (from contact attributes stamped by the inbound flow) plus past contacts
// (from the console read API / knktr-contact-history-dev). Field names are
// read defensively because the reader and writer evolved separately.
// D26 (2026-09-18): history is keyed <tenant>#<caller>, and the tenant is the
// contact's own — see lib/callerHistory.js for why it is never a build constant.
export default function HistoryPanel() {
  const { display } = useContact();
  const phone = display?.customer?.phone || '';
  const tenantId = display?.tenantId || '';
  const contactId = display?.meta?.contactId || '';
  const [result, setResult] = useState(null);   // { state, rows, error } | null while loading
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let dead = false;
    setResult(null);
    setExpanded(false);                 // a new caller starts collapsed
    if (!phone) return undefined;
    loadCallerHistory({ dataUrl: DATA_URL, tenantId, phone, contactId, fetchFn: fetch, getHeaders: apiHeaders })
      .then(r => { if (!dead) setResult(r); });
    return () => { dead = true; };
  }, [phone, tenantId, contactId]);

  if (!display) return null;
  const prior = display.prior;
  if (!phone && !prior) return null;

  const list = result?.state === OK ? result.rows : [];
  const shown = expanded ? list : list.slice(0, VISIBLE_ROWS);
  const hidden = list.length - shown.length;

  return (
    <div className="hist-card">
      <div className="hist-head">
        <History size={13} /> Caller history
        {list.length > 0 && <span className="hist-count">{list.length}</span>}
      </div>
      {prior && (
        <div className="hist-prior">
          <span className="hist-prior-line">
            <Moon size={12} /> Tried to reach us after hours
            {prior.count && prior.count !== '0' ? ` (${prior.count}x)` : ''}
            {prior.at ? ` — last ${fmt(prior.at)}` : ''}
          </span>
          {prior.summary && <div className="hist-prior-sum">{prior.summary}</div>}
        </div>
      )}
      {result === null && phone && <div className="hist-empty">Loading…</div>}
      {result?.state === NO_TENANT && (
        <div className="hist-empty">History unavailable — no tenant on this contact.</div>
      )}
      {result?.state === ERROR && <div className="hist-empty hist-err">{result.error}</div>}
      {result?.state === EMPTY && (
        <div className="hist-empty">No previous contacts on record.</div>
      )}
      <div className={`hist-rows${expanded ? ' expanded' : ''}`}>
        {shown.map((r, i) => (
          <div className="hist-row" key={(r.contactId || '') + i}>
            <span className="hist-when">{fmt(r.at || r.contactTs)}</span>
            <span className="hist-chan">{r.channel || ''}</span>
            {/* 2026-09-22 (vault #224): whole-call duration, the agent's saved
                disposition, and who handled it (display name only). Each is
                shown only when the record has it; nothing is invented. */}
            {/* No duration on callback tasks (vault #227): a task's span is
                a wait, not a call. */}
            {rowDuration(r) && (
              <span className="hist-dur" title="Whole call: AI, queue and agent">{rowDuration(r)}</span>
            )}
            {r.disposition && <span className="hist-disp">{r.disposition}</span>}
            {r.agentName && <span className="hist-agent">{r.agentName}</span>}
            {displaySummary(r.aiSummary || r.summary) && (
              <span className="hist-sum">{displaySummary(r.aiSummary || r.summary)}</span>
            )}
          </div>
        ))}
      </div>
      {list.length > VISIBLE_ROWS && (
        <button
          type="button"
          className="hist-more"
          onClick={() => setExpanded(e => !e)}
          aria-expanded={expanded}
        >
          {expanded
            ? <><ChevronUp size={12} /> Show fewer</>
            : <><ChevronDown size={12} /> Show {hidden} more</>}
        </button>
      )}
    </div>
  );
}
